import { queryClient } from '@/queryClient'
import { MutationFunction, UseMutationOptions, UseMutationResult, useMutation } from '@tanstack/react-query'
import { AxiosResponse, ResponseType } from 'axios'
import qs from 'query-string'
import { createApiRequest } from './createApiRequest'

// Definition of mutation options that include url, method, additional API options, and keys to refetch
interface DefinitionMutationOptions extends UseMutationOptions {
    url: string
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'
    apiOptions?: {
        responseType?: ResponseType
        headers?: Record<string, string>
    }
    keysToRefetch?: string[][]
}

// Definition of options for using the mutation that include query parameters, keys to refetch, and lifecycle hooks
interface UsageMutationOptions<TData> {
    queryParams?: Record<string, string>
    pathParams?: Record<string, string>
    refetch?: string[][]
    onSuccess?: (data: TData, ...arg: unknown[]) => void
    onError?: (...args: unknown[]) => void
    onSettled?: (...args: unknown[]) => void
    onMutate?: (...args: unknown[]) => void
    apiOptions?: {
        responseType?: ResponseType
        headers?: Record<string, string>
        onUploadProgress?: (progressEvent: ProgressEvent) => void
    }
}

// Payload type definition
export type PayloadType = Record<string, unknown> | FormData | undefined

/** Optional per-call query string params (e.g. disconnect?scope=workspace). */
export type MutationQueryParams = Record<string, string | undefined>

export type MutationPathParams = Record<string, string>

function splitMutationVariables(variables?: PayloadType): {
    queryParams: MutationQueryParams
    pathParams: MutationPathParams
    body: PayloadType
} {
    if (!variables || variables instanceof FormData) {
        return { queryParams: {}, pathParams: {}, body: variables }
    }
    if (!('queryParams' in variables) && !('pathParams' in variables)) {
        return { queryParams: {}, pathParams: {}, body: variables }
    }
    const {
        queryParams: variableQueryParams,
        pathParams: variablePathParams,
        ...rest
    } = variables as Record<string, unknown>
    const queryParams =
        variableQueryParams && typeof variableQueryParams === 'object' && !Array.isArray(variableQueryParams)
            ? (variableQueryParams as MutationQueryParams)
            : {}
    const pathParams =
        variablePathParams && typeof variablePathParams === 'object' && !Array.isArray(variablePathParams)
            ? Object.fromEntries(
                  Object.entries(variablePathParams).filter(
                      (entry): entry is [string, string] => typeof entry[1] === 'string'
                  )
              )
            : {}
    const body = Object.keys(rest).length > 0 ? (rest as Record<string, unknown>) : undefined
    return { queryParams, pathParams, body }
}

function mergeQueryParamStrings(base?: string, extra?: MutationQueryParams): string {
    const merged: Record<string, string> = {}
    if (base) {
        for (const [key, value] of Object.entries(qs.parse(base))) {
            if (typeof value === 'string') merged[key] = value
        }
    }
    if (extra) {
        for (const [key, value] of Object.entries(extra)) {
            if (value != null && value !== '') merged[key] = value
        }
    }
    return qs.stringify(merged)
}

// The createMutation function that takes the definition options and returns a new function that takes usage options
// and returns a mutation result
export const createMutation = <TData>(
    definitionOptions: DefinitionMutationOptions
): ((arg: UsageMutationOptions<TData>) => UseMutationResult<TData, unknown, PayloadType, unknown>) => {
    const useMutationFn = (usageOptions: UsageMutationOptions<TData> = {}) => {
        const { url, keysToRefetch, method = 'POST', apiOptions: definitionApiOptions } = definitionOptions
        const {
            queryParams,
            pathParams = {},
            refetch: usageRefetch,
            apiOptions: usageApiOptions,
            ...restUsageOptions
        } = usageOptions

        // Interpolate path parameters in URL
        let finalUrl = url
        Object.entries(pathParams).forEach(([param, value]) => {
            finalUrl = finalUrl.replace(`:${param}`, value)
        })

        const mergedKeys = mergeKeysToRefetch(keysToRefetch, usageRefetch)

        const mergedConfigs = mergeOptions<TData>(restUsageOptions, definitionOptions)

        // Overwrite onSuccess to include invalidation of queries
        const success = mergedConfigs.onSuccess
        mergedConfigs.onSuccess = (...args) => {
            mergedKeys.forEach(key => {
                queryClient.invalidateQueries({
                    predicate: query => {
                        return key.some(k => query.queryKey.includes(k))
                    },
                })
            })

            success?.(...args)
        }
        const hookQueryParams = queryParams

        // Type of the mutation function
        const mutationFn: MutationFunction<TData, PayloadType> = (variables?: PayloadType) => {
            const {
                queryParams: variableQueryParams,
                pathParams: variablePathParams,
                body,
            } = splitMutationVariables(variables)
            const params = mergeQueryParamStrings(
                hookQueryParams ? qs.stringify(hookQueryParams) : undefined,
                variableQueryParams
            )
            return createApiRequest<AxiosResponse<TData>>({
                url: Object.entries(variablePathParams).reduce(
                    (urlValue, [param, value]) => urlValue.replace(`:${param}`, value),
                    finalUrl
                ),
                data: body,
                method,
                queryParams: params || undefined,
                apiOptions: {
                    ...usageApiOptions,
                    ...definitionApiOptions,
                },
            }).then(response => response as TData)
        }

        return useMutation<TData, unknown, PayloadType, unknown>({
            mutationFn,
            ...mergedConfigs,
        })
    }

    return useMutationFn
}

// Merge keys for refetching. If keys exist in both the definition and usage options, they are combined.
const mergeKeysToRefetch = (keysToRefetch?: string[][], refetch?: string[][]) => {
    let mergedKeys: string[][] = []

    if (Array.isArray(keysToRefetch) && keysToRefetch.length > 0) {
        mergedKeys = [...mergedKeys, ...keysToRefetch]
    }

    if (Array.isArray(refetch) && refetch.length > 0) {
        mergedKeys = [...mergedKeys, ...refetch]
    }

    return mergedKeys
}

// Merge usage and definition options, with usage options taking precedence
const mergeOptions = <T>(usageOptions?: UseMutationOptions<T>, definitionOptions?: UseMutationOptions) => {
    const mergedOptions = {
        ...definitionOptions,
        ...usageOptions,
    }

    return mergedOptions as UseMutationOptions<T, unknown, PayloadType, unknown>
}
